import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { bookingServiceAPI, Booking } from '../services/bookingServiceAPI';
import { ticketService } from '../services/ticketService';
import QRScanner from '../components/QRScanner';
import '../styles/PassPage.css';

function PassPage() {
  const { bookingCode } = useParams<{ bookingCode?: string }>();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanningPaused, setScanningPaused] = useState(false);
  const [scanResult, setScanResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [usedTickets, setUsedTickets] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingCode) {
      loadBookingData();
    }
  }, [bookingCode]);

  const loadBookingData = async () => {
    try {
      setLoading(true);
      const { booking, tickets } = await bookingServiceAPI.getBooking(bookingCode!);
      if (booking) {
        setBooking(booking);
        // حساب التذاكر المستخدمة
        const usedCount = tickets.filter((t: any) => t.used).length;
        setUsedTickets(usedCount);
      }
    } catch (error) {
      console.error('Error loading booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (qrText: string) => {
    // إيقاف المسح مؤقتاً لمنع المسح المتكرر - لكن نبقي الكاميرا مفتوحة
    setScanningPaused(true);
    
    try {
      // التحقق من صلاحية التذكرة مع bookingCode
      const validation = await ticketService.validateTicket(qrText, bookingCode);
      
      if (validation.valid) {
        // استخدام التذكرة مع bookingCode
        const useResult = await ticketService.useTicket(qrText, bookingCode);
        if (useResult.success) {
          setScanResult('success');
          setUsedTickets(usedTickets + 1);
          // إعادة تعيين بعد 3 ثواني وإغلاق الكاميرا
          setTimeout(() => {
            setScanResult(null);
            setScanning(false); // إغلاق الكاميرا بعد عرض الرسالة
            setScanningPaused(false);
          }, 3000);
        } else {
          setScanResult('error');
          setErrorMessage(useResult.message || 'خطأ في استخدام التذكرة');
          setTimeout(() => {
            setScanResult(null);
            setErrorMessage('');
            setScanning(false); // إغلاق الكاميرا بعد عرض الرسالة
            setScanningPaused(false);
          }, 3000);
        }
      } else {
        setScanResult('error');
        setErrorMessage(validation.message || 'التذكرة غير صالحة أو لا تنتمي لهذا الحجز');
        // إعادة تعيين بعد 3 ثواني وإغلاق الكاميرا
        setTimeout(() => {
          setScanResult(null);
          setErrorMessage('');
          setScanning(false); // إغلاق الكاميرا بعد عرض الرسالة
          setScanningPaused(false);
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error scanning ticket:', error);
      setScanResult('error');
      setErrorMessage(error?.message || 'خطأ في معالجة التذكرة');
      setTimeout(() => {
        setScanResult(null);
        setErrorMessage('');
        setScanning(false); // إغلاق الكاميرا بعد عرض الرسالة
        setScanningPaused(false);
      }, 3000);
    }
  };

  const handleScanError = (error: string) => {
    console.error('Scanner error:', error);
    setErrorMessage(error);
  };


  if (loading) {
    return (
      <div className="pass-page">
        <div className="loading-container">
          <div className="loader"></div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="pass-page">
        <div className="error-container">
          <h2>الحجز غير موجود</h2>
          <Link to="/admin/confirmed-bookings" className="back-link">
            العودة للحجوزات
          </Link>
        </div>
      </div>
    );
  }

  const totalTickets = booking.totalTickets || 0;
  const remainingTickets = totalTickets - usedTickets;

  return (
    <motion.div 
      className="pass-page-simplified"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* معلومات جانبية صغيرة */}
      <div className="sidebar-info">
        <Link to="/admin/confirmed-bookings" className="back-btn">
          ←
        </Link>
        <div className="booking-code">#{booking.bookingCode}</div>
        <div className="customer-name">{booking.customerName}</div>
        {booking.bookingType === 'perPhoto' && (
          <div className="tickets-mini">
            <div className="mini-stat">
              <span className="mini-label">متبقي</span>
              <span className="mini-value">{remainingTickets}</span>
            </div>
            <div className="mini-divider">/</div>
            <div className="mini-stat">
              <span className="mini-label">إجمالي</span>
              <span className="mini-value">{totalTickets}</span>
            </div>
          </div>
        )}
      </div>

      {/* المحتوى الرئيسي */}
      <div className="main-scanner">
        <div className="scanner-area">
          {!scanning ? (
            <motion.button 
              className="big-scan-button"
              onClick={() => setScanning(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="scan-icon">📷</div>
              <span>اضغط لمسح التذكرة</span>
            </motion.button>
          ) : (
            <motion.div 
              className="scanner-active"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <QRScanner
                onScan={handleScan}
                onError={handleScanError}
                active={scanning && !scanningPaused}
              />
              {scanningPaused && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  zIndex: 10
                }}>
                  معالجة التذكرة...
                </div>
              )}
              <button 
                className="stop-scan"
                onClick={() => {
                  setScanning(false);
                  setScanningPaused(false);
                }}
              >
                إيقاف المسح
              </button>
            </motion.div>
          )}
        </div>

        <AnimatePresence>
          {scanResult && (
            <motion.div 
              className={`result-overlay ${scanResult}`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {scanResult === 'success' ? (
                <div className="success-content">
                  <motion.div 
                    className="big-check"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 10,
                      delay: 0.1
                    }}
                  >
                    ✓
                  </motion.div>
                  <h2>تم بنجاح</h2>
                  <p>مرحباً بك في فوتوميستا</p>
                </div>
              ) : (
                <div className="error-content">
                  <div className="big-x">✗</div>
                  <h2>خطأ</h2>
                  <p>{errorMessage}</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {booking.bookingType === 'hourly' && (
          <div className="hourly-notice">
            <span className="notice-icon">⏰</span>
            <span>نظام الساعات - صور مفتوحة</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default PassPage;